import * as React from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { AlertCircle, Heart, Loader2, UserPlus2 } from "lucide-react";
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Switch,
  Textarea,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import {
  ApiError,
  type Parent,
  type ParentCreateInput,
  type ParentRelationship,
  type ParentUpdateInput,
} from "@eduviaos/shared";
import { useCreateParent, useUpdateParent, PARENT_RELATIONSHIPS } from "@/hooks/useParents";

/**
 * Phase J3.b — Parent create / edit sheet.
 *
 * Right-side drawer (Sheet) variant — same UX choice as `StudentEditorSheet`
 * and `EnquiryCreateSheet`: keeps the People list visible behind the
 * sheet so the IA admin can see where the new parent will land while
 * typing. Sheet > Dialog here because the form has ~13 fields organised
 * into three sections (Identity, Address, Verification & extras) — too
 * tall for a centered modal at 1080p.
 *
 * Validation strategy: local-first HTML required + length checks (name
 * 1–120, phone 1–20) so we never POST a guaranteed-422. BE-side errors
 * come back as `VALIDATION_ERROR` / `PARENT_DUPLICATE_PHONE` etc., which
 * land in the top-of-sheet error banner with a friendly message.
 *
 * Verification toggles (`is_phone_verified` / `is_email_verified`) only
 * surface in edit mode. The BE accepts them on PATCH so admins can mark
 * a parent's phone verified after an out-of-band call (e.g. confirming
 * over Zoom that they own the number) without forcing a re-OTP cycle.
 */

interface DraftState {
  name: string;
  relationship: ParentRelationship;
  email: string;
  phone: string;
  alt_phone: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  pincode: string;
  pan_reference: string;
  occupation: string;
  is_phone_verified: boolean;
  is_email_verified: boolean;
}

const EMPTY_DRAFT: DraftState = {
  name: "",
  relationship: "guardian",
  email: "",
  phone: "",
  alt_phone: "",
  address_line1: "",
  address_line2: "",
  city: "",
  state: "",
  pincode: "",
  pan_reference: "",
  occupation: "",
  is_phone_verified: false,
  is_email_verified: false,
};

function draftFromExisting(existing: Parent | null | undefined): DraftState {
  if (!existing) return EMPTY_DRAFT;
  return {
    name: existing.name,
    relationship: existing.relationship,
    email: existing.email ?? "",
    phone: existing.phone,
    alt_phone: existing.alt_phone ?? "",
    address_line1: existing.address_line1 ?? "",
    address_line2: existing.address_line2 ?? "",
    city: existing.city ?? "",
    state: existing.state ?? "",
    pincode: existing.pincode ?? "",
    pan_reference: existing.pan_reference ?? "",
    occupation: existing.occupation ?? "",
    is_phone_verified: existing.is_phone_verified,
    is_email_verified: existing.is_email_verified,
  };
}

function trimmedOrNull(value: string): string | null {
  const t = value.trim();
  return t.length > 0 ? t : null;
}

function buildCreatePayload(draft: DraftState): ParentCreateInput {
  const payload: ParentCreateInput = {
    name: draft.name.trim(),
    phone: draft.phone.trim(),
    relationship: draft.relationship,
  };
  const email = trimmedOrNull(draft.email);
  if (email) payload.email = email;
  const altPhone = trimmedOrNull(draft.alt_phone);
  if (altPhone) payload.alt_phone = altPhone;
  const addr1 = trimmedOrNull(draft.address_line1);
  if (addr1) payload.address_line1 = addr1;
  const addr2 = trimmedOrNull(draft.address_line2);
  if (addr2) payload.address_line2 = addr2;
  const city = trimmedOrNull(draft.city);
  if (city) payload.city = city;
  const state = trimmedOrNull(draft.state);
  if (state) payload.state = state;
  const pincode = trimmedOrNull(draft.pincode);
  if (pincode) payload.pincode = pincode;
  const pan = trimmedOrNull(draft.pan_reference);
  if (pan) payload.pan_reference = pan;
  const occupation = trimmedOrNull(draft.occupation);
  if (occupation) payload.occupation = occupation;
  return payload;
}

function buildUpdatePayload(draft: DraftState, existing: Parent): ParentUpdateInput {
  const payload: ParentUpdateInput = {};
  const name = draft.name.trim();
  if (name && name !== existing.name) payload.name = name;

  if (draft.relationship !== existing.relationship) payload.relationship = draft.relationship;

  const email = trimmedOrNull(draft.email);
  if ((existing.email ?? null) !== email) payload.email = email;

  const phone = draft.phone.trim();
  if (phone && phone !== existing.phone) payload.phone = phone;

  const altPhone = trimmedOrNull(draft.alt_phone);
  if ((existing.alt_phone ?? null) !== altPhone) payload.alt_phone = altPhone;

  const addr1 = trimmedOrNull(draft.address_line1);
  if ((existing.address_line1 ?? null) !== addr1) payload.address_line1 = addr1;

  const addr2 = trimmedOrNull(draft.address_line2);
  if ((existing.address_line2 ?? null) !== addr2) payload.address_line2 = addr2;

  const city = trimmedOrNull(draft.city);
  if ((existing.city ?? null) !== city) payload.city = city;

  const state = trimmedOrNull(draft.state);
  if ((existing.state ?? null) !== state) payload.state = state;

  const pincode = trimmedOrNull(draft.pincode);
  if ((existing.pincode ?? null) !== pincode) payload.pincode = pincode;

  const pan = trimmedOrNull(draft.pan_reference);
  if ((existing.pan_reference ?? null) !== pan) payload.pan_reference = pan;

  const occupation = trimmedOrNull(draft.occupation);
  if ((existing.occupation ?? null) !== occupation) payload.occupation = occupation;

  if (draft.is_phone_verified !== existing.is_phone_verified) {
    payload.is_phone_verified = draft.is_phone_verified;
  }
  if (draft.is_email_verified !== existing.is_email_verified) {
    payload.is_email_verified = draft.is_email_verified;
  }

  return payload;
}

export interface ParentEditorSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existing?: Parent | null;
  onSaved?: (saved: Parent) => void;
}

export function ParentEditorSheet({
  open,
  onOpenChange,
  existing,
  onSaved,
}: ParentEditorSheetProps) {
  const isEdit = Boolean(existing);
  const reduceMotion = useReducedMotion();

  const [draft, setDraft] = React.useState<DraftState>(() => draftFromExisting(existing));
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setDraft(draftFromExisting(existing));
      setSubmitError(null);
    }
  }, [open, existing]);

  const createMutation = useCreateParent();
  const updateMutation = useUpdateParent();
  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const trimmedName = draft.name.trim();
  const trimmedPhone = draft.phone.trim();
  const canSubmit =
    trimmedName.length > 0 &&
    trimmedName.length <= 120 &&
    trimmedPhone.length > 0 &&
    trimmedPhone.length <= 20 &&
    !isSubmitting;

  const handleField = <K extends keyof DraftState>(key: K, value: DraftState[K]) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (!canSubmit) return;

    try {
      if (existing) {
        const body = buildUpdatePayload(draft, existing);
        if (Object.keys(body).length === 0) {
          // Zero diff — close silently, no toast (no action taken).
          onOpenChange(false);
          return;
        }
        const saved = await updateMutation.mutateAsync({ id: existing.id, body });
        onSaved?.(saved);
        // Phase E1.b.2 — global toast for the page-level success
        // surface. In-sheet `role="alert"` banner stays for
        // server-side validation (PARENT_DUPLICATE_PHONE etc.).
        toast.success(`Saved changes to ${saved.name}.`);
        onOpenChange(false);
      } else {
        const body = buildCreatePayload(draft);
        const saved = await createMutation.mutateAsync(body);
        onSaved?.(saved);
        toast.success(`Added ${saved.name} to the roster.`);
        onOpenChange(false);
      }
    } catch (err) {
      const message =
        err instanceof ApiError
          ? friendlyApiMessage(err)
          : err instanceof Error
            ? err.message
            : "Couldn't save the parent. Please retry.";
      setSubmitError(message);
    }
  };

  const title = isEdit ? "Edit parent" : "Add parent";
  const description = isEdit
    ? `Update ${existing?.name ?? "this parent"}'s profile. Changes are audited.`
    : "Add a parent / guardian record for your institution. Linked students are managed on the student record.";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-[540px]"
        data-testid="parent-editor-sheet"
      >
        <SheetHeader className="border-border/60 border-b px-6 pb-4 pt-6">
          <div className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="bg-primary/10 text-primary mt-0.5 grid size-10 shrink-0 place-items-center rounded-lg"
            >
              {isEdit ? <Heart className="size-5" /> : <UserPlus2 className="size-5" />}
            </span>
            <div className="flex-1">
              <SheetTitle className="tracking-tight">{title}</SheetTitle>
              <SheetDescription className="mt-1 text-xs">{description}</SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <form
          onSubmit={handleSubmit}
          className="flex min-h-0 flex-1 flex-col"
          data-testid="parent-editor-form"
        >
          <div className="flex-1 overflow-y-auto px-6 py-5">
            <AnimatePresence>
              {submitError ? (
                <motion.div
                  initial={reduceMotion ? false : { opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={reduceMotion ? undefined : { opacity: 0, y: -4 }}
                  transition={{ duration: 0.16, ease: "easeOut" }}
                  role="alert"
                  className="border-destructive/30 bg-destructive/5 text-destructive mb-4 flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
                >
                  <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
                  <span className="flex-1">{submitError}</span>
                </motion.div>
              ) : null}
            </AnimatePresence>

            <Section title="Identity" subtitle="Name and phone are required; rest is optional.">
              <Field label="Full name" htmlFor="parent-name" required>
                <Input
                  id="parent-name"
                  value={draft.name}
                  maxLength={120}
                  required
                  autoFocus
                  onChange={(e) => handleField("name", e.target.value)}
                  placeholder="Anita Sharma"
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Relationship" htmlFor="parent-relationship" required>
                  <Select
                    value={draft.relationship}
                    onValueChange={(v) => handleField("relationship", v as ParentRelationship)}
                  >
                    <SelectTrigger id="parent-relationship">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PARENT_RELATIONSHIPS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Occupation" htmlFor="parent-occupation">
                  <Input
                    id="parent-occupation"
                    value={draft.occupation}
                    maxLength={120}
                    onChange={(e) => handleField("occupation", e.target.value)}
                    placeholder="Engineer"
                  />
                </Field>
              </div>
              <Field label="Email" htmlFor="parent-email">
                <Input
                  id="parent-email"
                  type="email"
                  value={draft.email}
                  onChange={(e) => handleField("email", e.target.value)}
                  placeholder="anita@example.com"
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Phone" htmlFor="parent-phone" required>
                  <Input
                    id="parent-phone"
                    type="tel"
                    value={draft.phone}
                    maxLength={20}
                    required
                    onChange={(e) => handleField("phone", e.target.value)}
                    placeholder="+91 90000 00000"
                  />
                </Field>
                <Field label="Alternate phone" htmlFor="parent-alt-phone">
                  <Input
                    id="parent-alt-phone"
                    type="tel"
                    value={draft.alt_phone}
                    maxLength={20}
                    onChange={(e) => handleField("alt_phone", e.target.value)}
                    placeholder="+91 90000 00001"
                  />
                </Field>
              </div>
            </Section>

            <Section
              title="Address"
              subtitle="Used by transport routing and physical-mail flows. Optional but recommended."
            >
              <Field label="Address line 1" htmlFor="parent-addr1">
                <Input
                  id="parent-addr1"
                  value={draft.address_line1}
                  maxLength={240}
                  onChange={(e) => handleField("address_line1", e.target.value)}
                  placeholder="House / flat / street"
                />
              </Field>
              <Field label="Address line 2" htmlFor="parent-addr2">
                <Input
                  id="parent-addr2"
                  value={draft.address_line2}
                  maxLength={240}
                  onChange={(e) => handleField("address_line2", e.target.value)}
                  placeholder="Landmark / colony"
                />
              </Field>
              <div className="grid grid-cols-3 gap-3">
                <Field label="City" htmlFor="parent-city">
                  <Input
                    id="parent-city"
                    value={draft.city}
                    maxLength={80}
                    onChange={(e) => handleField("city", e.target.value)}
                    placeholder="Pune"
                  />
                </Field>
                <Field label="State" htmlFor="parent-state">
                  <Input
                    id="parent-state"
                    value={draft.state}
                    maxLength={80}
                    onChange={(e) => handleField("state", e.target.value)}
                    placeholder="Maharashtra"
                  />
                </Field>
                <Field label="Pincode" htmlFor="parent-pincode">
                  <Input
                    id="parent-pincode"
                    value={draft.pincode}
                    maxLength={10}
                    inputMode="numeric"
                    onChange={(e) => handleField("pincode", e.target.value)}
                    placeholder="411001"
                  />
                </Field>
              </div>
            </Section>

            <Section
              title="Verification & extras"
              subtitle={
                isEdit
                  ? "Mark verified after confirming out-of-band (e.g. a call). Newly created parents start unverified."
                  : "Verification flags ship false on create. They become editable after the parent is saved."
              }
            >
              {isEdit ? (
                <div className="grid gap-3">
                  <ToggleField
                    label="Phone verified"
                    helper="Unblocks OTP-based parent portal flows."
                    checked={draft.is_phone_verified}
                    onCheckedChange={(v) => handleField("is_phone_verified", v)}
                  />
                  <ToggleField
                    label="Email verified"
                    helper="Required before account-recovery emails are sent."
                    checked={draft.is_email_verified}
                    onCheckedChange={(v) => handleField("is_email_verified", v)}
                  />
                </div>
              ) : null}
              <Field
                label="PAN reference"
                htmlFor="parent-pan"
                helper="Tokenized reference, not the raw number. Used by fee receipts when applicable."
              >
                <Textarea
                  id="parent-pan"
                  value={draft.pan_reference}
                  maxLength={80}
                  rows={2}
                  onChange={(e) => handleField("pan_reference", e.target.value)}
                />
              </Field>
            </Section>
          </div>

          <div className="border-border/60 bg-card/40 flex items-center justify-end gap-2 border-t px-6 py-4 backdrop-blur">
            <SheetClose asChild>
              <Button variant="ghost" type="button" disabled={isSubmitting}>
                Cancel
              </Button>
            </SheetClose>
            <Button type="submit" disabled={!canSubmit} data-testid="parent-editor-submit">
              {isSubmitting ? (
                <Loader2 aria-hidden="true" className={cn("size-4 animate-spin")} />
              ) : null}
              {isEdit ? "Save changes" : "Add parent"}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}

interface SectionProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}

function Section({ title, subtitle, children }: SectionProps) {
  return (
    <div className="mb-6 last:mb-0">
      <div className="mb-3">
        <h3 className="text-foreground text-sm font-semibold tracking-tight">{title}</h3>
        {subtitle ? <p className="text-muted-foreground mt-0.5 text-xs">{subtitle}</p> : null}
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

interface FieldProps {
  label: string;
  htmlFor: string;
  required?: boolean;
  helper?: string;
  children: React.ReactNode;
}

function Field({ label, htmlFor, required, helper, children }: FieldProps) {
  return (
    <div>
      <Label htmlFor={htmlFor} className="text-xs">
        {label}
        {required ? <span className="text-destructive ml-0.5">*</span> : null}
      </Label>
      <div className="mt-1">{children}</div>
      {helper ? <p className="text-muted-foreground mt-1 text-xs">{helper}</p> : null}
    </div>
  );
}

interface ToggleFieldProps {
  label: string;
  helper?: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}

function ToggleField({ label, helper, checked, onCheckedChange }: ToggleFieldProps) {
  return (
    <div className="border-border/60 bg-muted/30 flex items-center justify-between gap-3 rounded-md border px-3 py-2">
      <div className="min-w-0 flex-1">
        <p className="text-foreground text-xs font-medium">{label}</p>
        {helper ? <p className="text-muted-foreground mt-0.5 text-xs">{helper}</p> : null}
      </div>
      <Switch checked={checked} onCheckedChange={onCheckedChange} aria-label={label} />
    </div>
  );
}

function friendlyApiMessage(err: ApiError): string {
  switch (err.code) {
    case "PARENT_DUPLICATE_PHONE":
      return "A parent with that phone number already exists in your institution.";
    case "PARENT_DUPLICATE_EMAIL":
      return "A parent with that email already exists in your institution.";
    case "VALIDATION_ERROR":
      return err.message || "Some fields failed validation.";
    case "AZ_PERMISSION_DENIED":
      return "You don't have permission to save parents. Ask an admin to grant manage_students.";
    default:
      return err.message || "Couldn't save the parent. Please retry.";
  }
}
